import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import EmailIcon from '@mui/icons-material/Email'
import ImageIcon from '@mui/icons-material/Image'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import PrintIcon from '@mui/icons-material/Print'
import SendIcon from '@mui/icons-material/Send'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { toast } from 'react-toastify'
import { shareRosterPrintArtifact } from 'src/supportPortal/services/rosterManagementService'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

const SLOT_SCOPE_ALL = 'all'
const SLOT_SCOPE_FILLED = 'filled'
const FORMAT_PDF = 'pdf'
const FORMAT_IMAGE = 'image'

const EXPORT_PRINT_CSS = `
  .no-print,
  .no-print * {
    display: none !important;
    visibility: hidden !important;
  }
  [data-roster-print-scope="filled"] .roster-print-open-slot,
  [data-roster-print-scope="filled"] [data-roster-slot-kind="open"] {
    display: none !important;
    visibility: hidden !important;
  }
  .roster-table-container {
    overflow: visible !important;
    box-shadow: none !important;
    background: #fff !important;
  }
  .roster-speciality-group {
    background: #fff !important;
  }
`

function getResponseMessage(response, fallback) {
  return (
    response?.data?.message ||
    response?.response?.data?.message ||
    response?.message ||
    fallback
  )
}

function sanitizeFilePart(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
}

function canvasToBlob(canvas, type = 'image/png', quality = 0.95) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob)
      } else {
        reject(new Error('Unable to render roster image.'))
      }
    }, type, quality)
  })
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export default function RosterPrintShareDialog({
  open,
  onClose,
  title = 'Roster',
  targetSelector = '',
  rosterViewMode = 'daily',
  rosterDateLabel = '',
  targetSpecialityLabel = '',
  eventTypeLabel = '',
}) {
  const [slotScope, setSlotScope] = useState(SLOT_SCOPE_FILLED)
  const [attachmentFormat, setAttachmentFormat] = useState(FORMAT_PDF)
  const [sendToHumanSupport, setSendToHumanSupport] = useState(true)
  const [sendToCustomNumber, setSendToCustomNumber] = useState(false)
  const [customWhatsappNumber, setCustomWhatsappNumber] = useState('')
  const [sendEmailToHumanSupport, setSendEmailToHumanSupport] = useState(false)
  const [sendEmailToCustomAddress, setSendEmailToCustomAddress] = useState(false)
  const [customEmailAddress, setCustomEmailAddress] = useState('')
  const [caption, setCaption] = useState('')
  const [captionEdited, setCaptionEdited] = useState(false)
  const [busyAction, setBusyAction] = useState('')
  const [error, setError] = useState('')

  const buildDefaultCaption = useCallback((scope = SLOT_SCOPE_FILLED) => {
    const viewLabel = rosterViewMode === 'daily' ? 'Daily roster' : 'Weekly roster'
    const parts = [
      viewLabel,
      rosterDateLabel,
      targetSpecialityLabel,
      eventTypeLabel,
      scope === SLOT_SCOPE_FILLED ? 'filled slots only' : 'all slots',
    ].filter(Boolean)
    return parts.join(' - ')
  }, [eventTypeLabel, rosterDateLabel, rosterViewMode, targetSpecialityLabel])

  const defaultCaption = useMemo(
    () => buildDefaultCaption(slotScope),
    [buildDefaultCaption, slotScope],
  )

  useEffect(() => {
    if (open) {
      setSlotScope(SLOT_SCOPE_FILLED)
      setAttachmentFormat(FORMAT_PDF)
      setSendToHumanSupport(true)
      setSendToCustomNumber(false)
      setCustomWhatsappNumber('')
      setSendEmailToHumanSupport(false)
      setSendEmailToCustomAddress(false)
      setCustomEmailAddress('')
      setCaption(buildDefaultCaption(SLOT_SCOPE_FILLED))
      setCaptionEdited(false)
      setError('')
    }
  }, [buildDefaultCaption, open])

  useEffect(() => {
    if (open && !captionEdited) {
      setCaption(defaultCaption)
    }
  }, [captionEdited, defaultCaption, open])

  const getTargetNode = () => {
    if (!targetSelector) {
      return document.querySelector('.roster-speciality-group')
    }
    return document.querySelector(targetSelector)
  }

  const filenameFor = (format) => {
    const fileBase = [
      'aumhum-roster',
      rosterViewMode,
      sanitizeFilePart(rosterDateLabel),
      sanitizeFilePart(targetSpecialityLabel),
      sanitizeFilePart(eventTypeLabel),
      slotScope,
    ].filter(Boolean).join('-')
    return `${fileBase || 'aumhum-roster'}.${format === FORMAT_PDF ? 'pdf' : 'png'}`
  }

  const withRosterPrintState = async (action) => {
    const previousScope = document.body.getAttribute('data-roster-print-scope')
    document.body.setAttribute('data-roster-print-scope', slotScope)
    try {
      return await action()
    } finally {
      if (previousScope) {
        document.body.setAttribute('data-roster-print-scope', previousScope)
      } else {
        document.body.removeAttribute('data-roster-print-scope')
      }
    }
  }

  const handleBrowserPrint = async () => {
    setError('')
    const targetNode = getTargetNode()
    if (!targetNode) {
      setError('Unable to find the roster table to print.')
      return
    }

    setBusyAction('print')
    await withRosterPrintState(async () => {
      const allGroups = Array.from(document.querySelectorAll('.roster-speciality-group'))
      allGroups.forEach((group) => {
        if (group !== targetNode) {
          group.setAttribute('data-print-hidden', 'true')
          group.style.display = 'none'
        }
      })

      const style = document.createElement('style')
      style.id = 'roster-print-dialog-style'
      style.textContent = `
        @page { size: landscape; margin: 0.3cm; }
        ${EXPORT_PRINT_CSS}
        @media print {
          .roster-speciality-group[data-print-hidden="true"] {
            display: none !important;
            visibility: hidden !important;
          }
        }
      `
      document.head.appendChild(style)
      window.print()

      setTimeout(() => {
        allGroups.forEach((group) => {
          group.removeAttribute('data-print-hidden')
          group.style.display = ''
        })
        document.getElementById('roster-print-dialog-style')?.remove()
        setBusyAction('')
      }, 800)
    })
  }

  const captureRosterCanvas = async () => {
    const targetNode = getTargetNode()
    if (!targetNode) {
      throw new Error('Unable to find the roster table to export.')
    }

    const html2canvas = (await import('html2canvas')).default
    return withRosterPrintState(() => html2canvas(targetNode, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      allowTaint: true,
      scrollX: 0,
      scrollY: -window.scrollY,
      windowWidth: Math.max(document.documentElement.scrollWidth, targetNode.scrollWidth),
      windowHeight: Math.max(document.documentElement.scrollHeight, targetNode.scrollHeight),
      onclone: (clonedDocument) => {
        clonedDocument.body.setAttribute('data-roster-print-scope', slotScope)
        const clonedStyle = clonedDocument.createElement('style')
        clonedStyle.textContent = EXPORT_PRINT_CSS
        clonedDocument.head.appendChild(clonedStyle)
        const clonedTarget = targetSelector ? clonedDocument.querySelector(targetSelector) : null
        if (clonedTarget) {
          clonedTarget.style.width = `${targetNode.scrollWidth}px`
          clonedTarget.style.background = '#fff'
        }
      },
    }))
  }

  const buildImageBlob = async () => {
    const canvas = await captureRosterCanvas()
    try {
      return await canvasToBlob(canvas, 'image/png', 0.95)
    } finally {
      const ctx = canvas.getContext('2d')
      ctx?.clearRect(0, 0, canvas.width, canvas.height)
      canvas.width = 0
      canvas.height = 0
    }
  }

  const buildPdfBlob = async () => {
    const canvas = await captureRosterCanvas()
    try {
      const { jsPDF } = await import('jspdf')
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const margin = 18
      const imgWidth = pageWidth - margin * 2
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      const imgData = canvas.toDataURL('image/png')

      let renderedHeight = 0
      pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight)
      renderedHeight += pageHeight - margin * 2

      while (renderedHeight < imgHeight) {
        pdf.addPage('a4', 'landscape')
        pdf.addImage(imgData, 'PNG', margin, margin - renderedHeight, imgWidth, imgHeight)
        renderedHeight += pageHeight - margin * 2
      }

      return pdf.output('blob')
    } finally {
      const ctx = canvas.getContext('2d')
      ctx?.clearRect(0, 0, canvas.width, canvas.height)
      canvas.width = 0
      canvas.height = 0
    }
  }

  const buildArtifactBlob = () => (
    attachmentFormat === FORMAT_PDF ? buildPdfBlob() : buildImageBlob()
  )

  const handleDownload = async () => {
    setError('')
    setBusyAction('download')
    try {
      const blob = await buildArtifactBlob()
      downloadBlob(blob, filenameFor(attachmentFormat))
    } catch (err) {
      setError(err?.message || 'Unable to prepare the roster file.')
    } finally {
      setBusyAction('')
    }
  }

  const handleSend = async () => {
    setError('')
    const hasWhatsappRecipient = sendToHumanSupport || sendToCustomNumber
    const hasEmailRecipient = sendEmailToHumanSupport || sendEmailToCustomAddress
    if (!hasWhatsappRecipient && !hasEmailRecipient) {
      setError('Select at least one WhatsApp or email recipient.')
      return
    }
    if (sendToCustomNumber && !customWhatsappNumber.trim()) {
      setError('Enter the WhatsApp number to send to.')
      return
    }
    if (sendEmailToCustomAddress && !customEmailAddress.trim()) {
      setError('Enter the email address to send to.')
      return
    }

    setBusyAction('send')
    try {
      const blob = await buildArtifactBlob()
      const mimeType = attachmentFormat === FORMAT_PDF ? 'application/pdf' : 'image/png'
      const fileName = filenameFor(attachmentFormat)
      const file = blob.type === mimeType ? blob : blob.slice(0, blob.size, mimeType)
      const response = await shareRosterPrintArtifact({
        file,
        fileName,
        caption: caption || defaultCaption,
        customWhatsappNumber,
        sendToCustomNumber,
        sendToHumanSupport,
        customEmailAddress,
        sendEmailToCustomAddress,
        sendEmailToHumanSupport,
        attachmentFormat,
        slotScope,
        rosterViewMode,
        rosterDateLabel,
        targetSpecialityLabel,
        eventTypeLabel,
      })

      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const data = getDataMapFromCommonResponse(response)
        const attempted = data?.attempted || data?.results?.length || 1
        toast.success(`Roster sent to ${attempted} recipient${attempted === 1 ? '' : 's'}.`)
        onClose?.()
      } else {
        setError(getResponseMessage(response, 'Unable to send the roster right now.'))
      }
    } catch (err) {
      setError(err?.message || 'Unable to send the roster right now.')
    } finally {
      setBusyAction('')
    }
  }

  const isBusy = !!busyAction

  return (
    <Dialog open={open} onClose={isBusy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PrintIcon color="primary" />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Print or send roster
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      {isBusy && <LinearProgress />}
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {rosterViewMode !== 'daily' && (
            <Alert severity="info">
              Daily roster is the usual handoff print. This will still export the current {rosterViewMode} view.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}

          <FormControl>
            <FormLabel sx={{ fontWeight: 700 }}>Slots to include</FormLabel>
            <RadioGroup
              value={slotScope}
              onChange={(event) => setSlotScope(event.target.value)}
            >
              <FormControlLabel
                value={SLOT_SCOPE_ALL}
                control={<Radio />}
                label="All slots, including open time ranges"
              />
              <FormControlLabel
                value={SLOT_SCOPE_FILLED}
                control={<Radio />}
                label="Only filled slots"
              />
            </RadioGroup>
          </FormControl>

          <FormControl>
            <FormLabel sx={{ fontWeight: 700 }}>Attachment format</FormLabel>
            <RadioGroup
              row
              value={attachmentFormat}
              onChange={(event) => setAttachmentFormat(event.target.value)}
            >
              <FormControlLabel
                value={FORMAT_PDF}
                control={<Radio />}
                label={<Stack direction="row" spacing={0.75} alignItems="center"><PictureAsPdfIcon fontSize="small" /> <span>PDF</span></Stack>}
              />
              <FormControlLabel
                value={FORMAT_IMAGE}
                control={<Radio />}
                label={<Stack direction="row" spacing={0.75} alignItems="center"><ImageIcon fontSize="small" /> <span>Image</span></Stack>}
              />
            </RadioGroup>
          </FormControl>

          <Box>
            <Typography sx={{ fontWeight: 700, mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <WhatsAppIcon fontSize="small" color="success" />
              WhatsApp recipients
            </Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendToHumanSupport}
                    onChange={(event) => setSendToHumanSupport(event.target.checked)}
                  />
                }
                label="Human Support number"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendToCustomNumber}
                    onChange={(event) => setSendToCustomNumber(event.target.checked)}
                  />
                }
                label="Provided WhatsApp number"
              />
              <TextField
                label="WhatsApp number"
                placeholder="+919876543210"
                value={customWhatsappNumber}
                onChange={(event) => setCustomWhatsappNumber(event.target.value)}
                size="small"
                disabled={!sendToCustomNumber}
                helperText="Use E.164 format or a 10-digit Indian mobile number."
              />
            </Stack>
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 700, mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <EmailIcon fontSize="small" color="primary" />
              Email recipients
            </Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendEmailToHumanSupport}
                    onChange={(event) => setSendEmailToHumanSupport(event.target.checked)}
                  />
                }
                label="Human Support email"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendEmailToCustomAddress}
                    onChange={(event) => setSendEmailToCustomAddress(event.target.checked)}
                  />
                }
                label="Provided email address"
              />
              <TextField
                label="Email address"
                placeholder="support@example.com"
                value={customEmailAddress}
                onChange={(event) => setCustomEmailAddress(event.target.value)}
                size="small"
                disabled={!sendEmailToCustomAddress}
                helperText="Use one email address, or comma-separate multiple recipients."
              />
            </Stack>
          </Box>

          <TextField
            label="Message"
            value={caption}
            onChange={(event) => {
              setCaptionEdited(true)
              setCaption(event.target.value)
            }}
            multiline
            minRows={2}
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} disabled={isBusy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleBrowserPrint}
          disabled={isBusy}
          startIcon={<PrintIcon />}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Print
        </Button>
        <Button
          onClick={handleDownload}
          disabled={isBusy}
          startIcon={<DownloadIcon />}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Download
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={isBusy}
          startIcon={<SendIcon />}
          sx={{ textTransform: 'none', fontWeight: 800 }}
        >
          Send selected
        </Button>
      </DialogActions>
    </Dialog>
  )
}
